import { formatDeviceId, type OrganizationRef } from '@viewer/protocol';
import type { HostConnection } from '../../lib/tauri/host';
import { StatusDot } from '../../ui/StatusDot';
import { peerStateLabel, peerStateTone } from '../../ui/peerState';
import styles from './HostSessionScreen.module.css';

interface Props {
  connections: HostConnection[];
  /** Encerra só estas sessões; as outras continuam. */
  onDisconnect(sessionIds: string[]): void;
}

interface CompanyGroup {
  organization: OrganizationRef | null;
  connections: HostConnection[];
}

/** Agrupa por empresa, na ordem em que cada uma entrou; quem entrou sem empresa fica junto. */
function byCompany(connections: HostConnection[]): CompanyGroup[] {
  const groups: CompanyGroup[] = [];
  for (const connection of connections) {
    const id = connection.organization?.id ?? null;
    const group = groups.find((g) => (g.organization?.id ?? null) === id);
    if (group) group.connections.push(connection);
    else groups.push({ organization: connection.organization, connections: [connection] });
  }
  return groups;
}

export function HostSessionScreen({ connections, onDisconnect }: Props) {
  const groups = byCompany(connections);

  if (connections.length === 0) {
    return (
      <section className={styles.card}>
        <p className={`${styles.eyebrow} ${styles.eyebrowIdle}`}>Conexões</p>
        <h2 className={styles.title}>Nenhuma conexão ativa</h2>
        <p className={styles.empty}>
          Quando alguém acessar este computador, a pessoa aparece aqui e você pode desconectá-la.
        </p>
      </section>
    );
  }

  return (
    <section className={styles.card}>
      <p className={styles.eyebrow}>Sessão em andamento</p>
      <h2 className={styles.title}>Sua tela está sendo compartilhada</h2>
      <p className={styles.peer}>
        {connections.length === 1 ? '1 pessoa conectada' : `${connections.length} pessoas conectadas`}
      </p>

      {groups.map(({ organization, connections: people }) => (
        <div key={organization?.id ?? 'sem-empresa'} className={styles.company}>
          <div className={styles.companyHeader}>
            <h3 className={styles.companyName}>{organization?.name ?? 'Sem empresa'}</h3>
            {organization && (
              <button
                type="button"
                className={styles.disconnect}
                title={`Desconecta todas as pessoas de ${organization.name}`}
                onClick={() => onDisconnect(people.map((c) => c.sessionId))}
              >
                Desconectar empresa
              </button>
            )}
          </div>
          <ul className={styles.people}>
            {people.map((connection) => (
              <li key={connection.sessionId} className={styles.person}>
                <div className={styles.who}>
                  <strong>{connection.user?.name ?? connection.peerName}</strong>
                  <span className={styles.detail}>
                    {connection.user ? `${connection.user.email} · ${connection.peerName} · ` : ''}ID{' '}
                    {formatDeviceId(connection.peerId)}
                  </span>
                  <span className={styles.status}>
                    <StatusDot tone={peerStateTone(connection.state)} /> {peerStateLabel(connection.state)}
                    {connection.access === 'view' && ' · Somente visualização'}
                  </span>
                </div>
                <button
                  type="button"
                  className={styles.disconnect}
                  onClick={() => onDisconnect([connection.sessionId])}
                >
                  Desconectar
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}

      <button type="button" className={styles.end} onClick={() => onDisconnect(connections.map((c) => c.sessionId))}>
        {connections.length === 1 ? 'Encerrar sessão' : 'Desconectar todos'}
      </button>
    </section>
  );
}
